/**
 * Semantic-First Multilingual Hyperscript Types
 *
 * This module defines the canonical semantic representation that all languages
 * parse to and render from. The semantic layer is language-neutral - it captures
 * the MEANING of hyperscript commands independent of surface syntax.
 */

// Re-export the SemanticRole type from local grammar-types for consistency
export type {
  SemanticRole,
  WordOrder,
  AdpositionType,
  MorphologyType,
  GrammaticalMarker,
  LanguageProfile,
} from './types/grammar-types';

import type { SemanticRole } from './types/grammar-types';

// Re-export Diagnostic from framework for structured error reporting (Phase 3.4)
export type { Diagnostic, DiagnosticSeverity } from '@lokascript/framework';

// =============================================================================
// Action Types
// =============================================================================

/**
 * Canonical action names (English-based internally, but not visible to users)
 * These map to hyperscript commands and are used in the semantic AST.
 */
export type ActionType =
  // Class/Attribute operations
  | 'toggle'
  | 'add'
  | 'remove'
  // Content operations
  | 'put'
  | 'append'
  | 'prepend'
  | 'take'
  | 'make'
  | 'clone'
  | 'swap'
  | 'morph'
  // Variable operations
  | 'set'
  | 'get'
  | 'increment'
  | 'decrement'
  | 'log'
  | 'copy'
  | 'pick'
  | 'beep'
  // Dialogs
  | 'ask'
  | 'answer'
  // Visibility
  | 'show'
  | 'hide'
  | 'transition'
  | 'viewTransition'
  // Events
  | 'on'
  | 'trigger'
  | 'send'
  // DOM focus / form state
  | 'focus'
  | 'blur'
  | 'empty'
  | 'open'
  | 'close'
  | 'select'
  | 'clear'
  | 'reset'
  | 'breakpoint'
  // Navigation
  | 'go'
  | 'scroll'
  | 'push'
  | 'replace'
  // DOM partials
  | 'process'
  // Async
  | 'wait'
  | 'fetch'
  | 'settle'
  // Animation/Measurement
  | 'measure'
  // Behavior system
  | 'install'
  // Control flow
  | 'if'
  | 'unless'
  | 'else'
  | 'repeat'
  | 'for'
  | 'while'
  | 'continue'
  | 'break'
  | 'exit'
  | 'halt'
  | 'throw'
  | 'call'
  | 'return'
  // Templates
  | 'render'
  // Advanced
  | 'js'
  | 'tell'
  | 'default'
  | 'init'
  | 'behavior'
  // Reactivity
  | 'bind'
  | 'live'
  | 'when'
  // Realtime / streaming
  | 'eventsource'
  | 'socket'
  | 'worker'
  // Service workers
  | 'intercept'
  // Meta (for compound nodes)
  | 'compound';

// =============================================================================
// Semantic Values
// =============================================================================

/**
 * A semantic value represents a typed piece of data in a semantic node.
 * Values are language-neutral - they capture what something IS, not how it's written.
 */
export type SemanticValue =
  LiteralValue | SelectorValue | ReferenceValue | PropertyPathValue | ExpressionValue | FlagValue;

/**
 * Set on a value the parser injected from a schema/extraction-rule `default`
 * rather than captured from the source text. Renderers suppress implicit
 * values (they were never written); authored values must survive round-trips
 * — `add .active to me` renders its `to me`, bare `add .active` stays bare.
 */
export interface ImplicitTaggable {
  readonly implicit?: true;
}

/**
 * Where in the parsed INPUT this value's surface text sat.
 *
 * Optional and advisory: a value the parser materialized from a schema
 * `default` was never written down, and several capture paths build a value
 * from a synthesized string rather than a token run — those carry no position,
 * and an absent span is honest where a guessed one would not be.
 *
 * The offsets are relative to the string handed to the parser, NOT to any
 * larger document. A caller that parsed a SLICE (as `@hyperfixi/core` does —
 * it hands the semantic path everything from the command token onward) owns
 * the translation to absolute offsets.
 */
export interface SourceSpanned {
  readonly position?: SourcePosition;
}

/**
 * Expected value types for role tokens.
 * Shared between RoleSpec (command-schemas) and RolePatternToken.
 */
export type ExpectedType = SemanticValue['type'];

export interface LiteralValue extends ImplicitTaggable, SourceSpanned {
  readonly type: 'literal';
  readonly value: string | number | boolean;
  readonly dataType?: 'string' | 'number' | 'boolean' | 'duration';
  /**
   * A naked URL with a `${…}` span (`fetch /search?q=${my value}`). Core
   * interpolates it; a quoted string interpolates on neither engine, and
   * upstream sends even an unspaced naked `${x}` literally. So it renders as
   * written, naked, and reaches core as the template core's parser builds.
   */
  readonly interpolates?: true;
  /**
   * A string the author wrote in quotes. An event name renders bare
   * (`send hello`), unless it was quoted: then it is written back as written
   * (`send "hello"`, `trigger "my event"`). Bare, a name with a space or a
   * colon reads as something else (P12), and a translation keeps what the
   * author wrote.
   */
  readonly quoted?: true;
}

export interface SelectorValue extends ImplicitTaggable, SourceSpanned {
  readonly type: 'selector';
  readonly value: string; // The CSS selector: #id, .class, [attr], etc.
  readonly selectorKind: 'id' | 'class' | 'attribute' | 'element' | 'complex';
  /**
   * The element a query is scoped to: `<button/> in me`, `<input/> in closest
   * <form/>`. A reference, a selector, or a `closest …` expression.
   */
  readonly scope?: SemanticValue;
}

export interface ReferenceValue extends ImplicitTaggable, SourceSpanned {
  readonly type: 'reference';
  readonly value:
    'me' | 'you' | 'it' | 'result' | 'event' | 'target' | 'body' | 'document' | 'window' | 'detail';
}

export interface PropertyPathValue extends ImplicitTaggable, SourceSpanned {
  readonly type: 'property-path';
  readonly object: SemanticValue;
  readonly property: string;
  /**
   * Which SURFACE the author wrote, when the parser knows it.
   *
   * `#input.value` and `#input's value` are the same access semantically and
   * were the same value structurally, so the renderer had to guess — and it
   * guessed possessive for both, turning `#input.value` into es `#input de
   * valor`, which no target parser binds back as a property path.
   *
   * A renderer-side heuristic cannot recover this: `its.name` (dot) and `my
   * value` (possessive) both have `reference` objects, so the object's type
   * does not discriminate. Recording it at the one place that knows — the
   * matcher, which matched a specific surface — is the only honest fix.
   *
   * Optional: absent means "not recorded", and the renderer keeps its existing
   * possessive construction, so values built anywhere else are unaffected.
   */
  readonly access?: 'dot' | 'possessive';
}

export interface ExpressionValue extends ImplicitTaggable, SourceSpanned {
  readonly type: 'expression';
  /** Raw expression string for complex expressions that need further parsing */
  readonly raw: string;
}

/**
 * A boolean flag value — present (+flag) or negated (~flag).
 * Used in declarative domains for no-value attributes like primary-key, not-null.
 */
export interface FlagValue extends ImplicitTaggable, SourceSpanned {
  readonly type: 'flag';
  readonly name: string;
  readonly enabled: boolean;
}

// =============================================================================
// Semantic Nodes
// =============================================================================

/**
 * Base interface for all semantic nodes.
 * Semantic nodes capture the MEANING of hyperscript constructs.
 */
export interface SemanticNode {
  readonly kind:
    | 'command'
    | 'event-handler'
    | 'conditional'
    | 'compound'
    | 'loop'
    | 'behavior'
    | 'def'
    | 'feature';
  readonly action: ActionType;
  readonly roles: ReadonlyMap<SemanticRole, SemanticValue>;
  readonly metadata?: SemanticMetadata;
  /** Structured diagnostics from parsing stages (Phase 3.4) */
  readonly diagnostics?: readonly import('@lokascript/framework').Diagnostic[];
}

/**
 * Metadata about the source of a semantic node.
 * Useful for debugging, error messages, and round-trip conversion.
 */
export interface SemanticMetadata {
  readonly sourceLanguage?: string;
  readonly sourceText?: string;
  readonly sourcePosition?: SourcePosition;
  readonly patternId?: string;
  /**
   * Confidence score for the parse (0-1).
   * Higher values indicate more certain matches.
   * - 1.0: Exact match with all roles captured
   * - 0.8-0.99: High confidence with minor uncertainty (stem matching, optional roles)
   * - 0.6-0.8: Medium confidence (morphological normalization, defaults applied)
   * - <0.6: Low confidence (may need fallback to traditional parser)
   */
  readonly confidence?: number;
}

export interface SourcePosition {
  readonly start: number;
  readonly end: number;
  readonly line?: number;
  readonly column?: number;
}

/**
 * A command semantic node - represents a single hyperscript command.
 */
export interface CommandSemanticNode extends SemanticNode {
  readonly kind: 'command';
  /**
   * A `wait for`'s events and timeouts, in order, when there is more to it than
   * the first event the `event` role holds: each event with the names it
   * destructures (`pointermove(clientX, clientY)`), or a timeout (`or 1s`).
   * Outside the roles on purpose: the wait patterns match and render the first
   * event only.
   */
  readonly waitAlternatives?: readonly WaitAlternative[];
  /** Where a `wait for` listens (`from document`); `me` when absent. */
  readonly waitSource?: SemanticValue;
  /**
   * `fetch … do not throw`: resolve on a non-2xx response instead of throwing
   * (core's `modifiers.doNotThrow`).
   */
  readonly doNotThrow?: boolean;
  /**
   * The position of a `go`/`scroll` destination, as written before its `of`:
   * `top` in `go to top of #d1`, `the bottom`, `top left`. Its first position
   * word is core's `modifiers.position`, and the destination `modifiers.of`.
   */
  readonly scrollPosition?: string;
  /** `smoothly` / `instantly` after a `go` or `scroll` (core's `modifiers.behavior`). */
  readonly scrollBehavior?: 'smoothly' | 'instantly';
  /** The direction of `scroll [<el>] up|down|left|right by <n>` (the engine's `direction`). */
  readonly scrollDirection?: 'up' | 'down' | 'left' | 'right';
  /**
   * A clause after the command that its pattern does not model (`when it
   * matches .doh`, `between .a and .b`), kept as written and written back as
   * written, in place of dropping it.
   */
  readonly verbatimClause?: string;
}

/** One alternative a `wait for` races: an event, or a timeout. */
export type WaitAlternative =
  { readonly event: string; readonly params?: readonly string[] } | { readonly duration: string };

/**
 * An event handler semantic node - represents "on [event] [commands]".
 */
/**
 * A handler's or a function's error clauses: `catch <name>` and what runs when
 * the body throws, and `finally` and what runs after it, thrown or not. Dropped,
 * a handler's error handling ran unconditionally after its body.
 */
export interface ErrorClauses {
  /** The name `catch` binds the error to. */
  readonly catchName?: string;
  /** What runs when the body throws. */
  readonly catchBody?: SemanticNode[];
  /** What runs after the body, thrown or not. */
  readonly finallyBody?: SemanticNode[];
}

export interface EventHandlerSemanticNode extends SemanticNode, ErrorClauses {
  readonly kind: 'event-handler';
  readonly action: 'on';
  readonly body: SemanticNode[];
  readonly eventModifiers?: EventModifiers;
  /**
   * Additional events for multi-event handlers (e.g., "on click or keydown").
   * The primary event is in the 'event' role; these are the additional "or" events.
   */
  readonly additionalEvents?: readonly SemanticValue[];
  /**
   * Event parameter names for destructuring.
   * E.g., for "on click(clientX, clientY)", this would be ['clientX', 'clientY']
   */
  readonly parameterNames?: readonly string[];
  /**
   * What upstream's `on` reads between the event and the body that the event
   * patterns do not model (`elsewhere`, a count `1 to 2`, `of attributes`,
   * `queue first`, `having threshold 0.1`), kept as written and written after
   * the event, in place of dropping it.
   */
  readonly headClause?: string;
}

export interface EventModifiers {
  readonly once?: boolean;
  /**
   * `once` written `on first <event>`, the form both engines run once. Absent,
   * core's `<event>.once` (upstream reads that as an event named so, which a
   * click never fires).
   */
  readonly onceAsFirst?: boolean;
  /**
   * `on every <event>`: each event runs its own copy of the handler, none
   * queued (upstream's `every`). Written `every` before the event in English
   * and leading in every other language, as `first` is.
   */
  readonly every?: boolean;
  readonly debounce?: number;
  readonly throttle?: number;
  readonly queue?: 'first' | 'last' | 'all' | 'none';
  readonly from?: SemanticValue; // Event source filter
}

/**
 * A conditional semantic node - represents "if [condition] then [body] else [body]".
 */
export interface ConditionalSemanticNode extends SemanticNode {
  readonly kind: 'conditional';
  readonly action: 'if';
  readonly thenBranch: SemanticNode[];
  readonly elseBranch?: SemanticNode[];
}

/**
 * A compound semantic node - represents multiple chained statements.
 */
export interface CompoundSemanticNode extends SemanticNode {
  readonly kind: 'compound';
  readonly statements: SemanticNode[];
  readonly chainType: 'then' | 'and' | 'async';
}

/**
 * Loop variant discriminant for different loop types.
 */
export type LoopVariant =
  | 'forever' // repeat forever
  | 'times' // repeat 5 times
  | 'for' // for item in collection
  | 'while' // while condition
  | 'until'; // until condition

/**
 * A loop semantic node - represents repeat/for/while loops.
 */
export interface LoopSemanticNode extends SemanticNode {
  readonly kind: 'loop';
  readonly action: 'repeat' | 'for' | 'while';
  /** The type of loop (forever, times, for, while, until) */
  readonly loopVariant: LoopVariant;
  /** Commands to execute in each iteration */
  readonly body: SemanticNode[];
  /** Loop variable name for 'for' loops (e.g., 'item' in 'for item in list') */
  readonly loopVariable?: string;
  /** Index variable name if specified (e.g., 'i' in 'for item in list index i') */
  readonly indexVariable?: string;
  /**
   * A `while`/`until` loop whose test follows its body (`repeat … until x end`):
   * the body runs once before the condition is read.
   */
  readonly bottomTested?: boolean;
}

/**
 * A command that carries its block: upstream's `tell <target> <commands> end` and
 * `start view transition [using "<type>"] <commands> end`. The clause walker nests
 * the body under the head as it nests a loop's, so a command after the block's
 * `end` stays after it. A head with no `body` is one whose extent the parse did
 * not see (its body is the statements after it, as the flat model always read
 * it); an empty `body` is a block its own `end` closed.
 */
export interface BlockCommandSemanticNode extends CommandSemanticNode {
  readonly action: 'tell' | 'viewTransition';
  readonly body: SemanticNode[];
}

/** `start view transition [using "<type>"] <commands> end`, its body attached. */
export interface ViewTransitionSemanticNode extends BlockCommandSemanticNode {
  readonly action: 'viewTransition';
}

/** The commands whose body the clause walker nests (BlockCommandSemanticNode). */
export const BLOCK_COMMAND_ACTIONS: ReadonlySet<string> = new Set(['tell', 'viewTransition']);

/** A `tell` or `start view transition` block, its body attached. */
export function isBlockCommand(node: SemanticNode): node is BlockCommandSemanticNode {
  return (
    node.kind === 'command' &&
    BLOCK_COMMAND_ACTIONS.has(node.action) &&
    Array.isArray((node as { body?: unknown }).body)
  );
}

/** A `start view transition … end` block, its body attached. */
export function isViewTransitionBlock(node: SemanticNode): node is ViewTransitionSemanticNode {
  return isBlockCommand(node) && node.action === 'viewTransition';
}

/**
 * A behavior semantic node — represents a `behavior Name(params) … end` block:
 * a reusable, named module of event handlers (and an optional init block). Unlike
 * the single-statement nodes above, this is a BLOCK construct decomposed by the
 * structural layer (see `parser/block-parser.ts`); its handlers are parsed by the
 * ordinary single-statement engine and re-assembled here.
 */
export interface BehaviorSemanticNode extends SemanticNode {
  readonly kind: 'behavior';
  readonly action: 'behavior';
  /** Behavior name (PascalCase identifier). */
  readonly name: string;
  /** Declared parameter names (`behavior Toggleable(cls, target)`). */
  readonly parameters: readonly string[];
  /** The behavior's event handlers, each a fully-parsed event-handler node. */
  readonly eventHandlers: EventHandlerSemanticNode[];
  /** Commands in the optional `init … end` block. */
  readonly initBlock?: SemanticNode[];
}

/**
 * A function-definition semantic node — represents a `def name(params) … end`
 * block. Like {@link BehaviorSemanticNode} it is a block construct decomposed by
 * the structural layer, but its body is a flat sequence of commands rather than
 * event handlers.
 */
export interface DefSemanticNode extends SemanticNode, ErrorClauses {
  readonly kind: 'def';
  // `action` stays the inherited ActionType (set to 'def' via the factory) — `def`
  // is a node KIND, not a command action, so it's kept out of the ActionType union
  // (mirrors `compound`); discrimination is on `kind`.
  /** Function name (may be namespaced, e.g. `utils.calculate`). */
  readonly name: string;
  /** Declared parameter names. */
  readonly parameters: readonly string[];
  /** The function body commands. */
  readonly body: SemanticNode[];
}

/** Block-structured feature actions — see {@link FeatureSemanticNode}. */
export type FeatureAction =
  'live' | 'when' | 'init' | 'eventsource' | 'socket' | 'worker' | 'intercept';

// (kept in the union above so `FeatureSemanticNode.action` covers every feature
// the fold handles; `worker`'s body is `def` sub-blocks rather than handlers.)

/**
 * A feature semantic node — a top-level block construct whose meaning lives in
 * its BODY rather than in head roles (`live … end`, `eventsource Name from url
 * … end end`, `socket Name url … end`, `worker Name … end end`, `intercept …
 * end`).
 *
 * Five of them declare `roles: []` + `bareKeyword: true` in their command
 * schema, so the generated pattern is a lone keyword literal. Before the
 * structural layer learned about them they matched that bare-keyword pattern at
 * Stage 2 and their entire body was dropped — at a vacuous confidence 1.0,
 * because `scoreRoleCoverage` returns 1 when a pattern declares no roles. This
 * node kind is what the fold produces instead; confidence is derived from the
 * body.
 *
 * The reactive observer `when <expr> changes … end` is the one feature WITH a
 * head role: the watched expression lives in `roles.condition` (the same role
 * the `if`/`unless`/`while` heads use). Its schema is `structuralOnly` — no
 * pattern is generated for it, because the REQUIRED trailing `changes` literal
 * is what bounds the multi-token expression and what keeps the reactive head
 * apart from the temporal `when {event}` handler patterns. Before the fold
 * learned it, those handler patterns claimed it and kept only the FIRST token
 * of the expression as the "event" (`when $a or $b changes` → `on $a`).
 *
 * `intercept` is the odd one out: its body is a configuration DSL (`precache …`,
 * `on /api/* use network-first`, `offline fallback …`), not hyperscript commands,
 * so the fold CONSUMES it without parsing and leaves `body` empty. Parsing it
 * would mint a phantom `on` event-handler and junk `use` actions.
 */
export interface FeatureSemanticNode extends SemanticNode {
  readonly kind: 'feature';
  readonly action: FeatureAction;
  /** Declared name, where the feature has one (`live`/`intercept` do not). */
  readonly name?: string;
  /** Parsed body statements. Always empty for `intercept` (opaque body). */
  readonly body: SemanticNode[];
  /**
   * `init immediately`: upstream runs the block as the element is read, not
   * after the page loads. The English word in every language, as `every`.
   */
  readonly immediately?: true;
}

// =============================================================================
// Language Patterns
// =============================================================================

/**
 * A pattern defines how a semantic structure appears in a specific language.
 * Patterns enable bidirectional conversion: parse (natural → semantic) and
 * render (semantic → natural).
 */
export interface LanguagePattern {
  /** Unique identifier for this pattern */
  readonly id: string;

  /** ISO 639-1 language code */
  readonly language: string;

  /** Which command this pattern matches */
  readonly command: ActionType;

  /** Priority for disambiguation (higher = checked first) */
  readonly priority: number;

  /** The pattern template with role placeholders */
  readonly template: PatternTemplate;

  /** Rules for extracting semantic roles from matched tokens */
  readonly extraction: ExtractionRules;

  /** Optional constraints on when this pattern applies */
  readonly constraints?: PatternConstraints;
  /**
   * The pattern reads a form the renderer does not write by choosing it
   * (wait's `esperar evento {event}`: the renderer writes the event word
   * itself, before a name no table knows). The renderer skips it.
   */
  readonly parseOnly?: boolean;
}

/**
 * A pattern template defines the expected token sequence.
 *
 * Template syntax:
 * - Literal tokens: "toggle", "を", "على"
 * - Role placeholders: {patient}, {target}, {destination}
 * - Optional groups: [on {target}]
 * - Alternatives in extraction (not in template string)
 *
 * Example templates:
 * - English: "toggle {patient} [on {target}]"
 * - Japanese: "{target} の {patient} を 切り替え"
 * - Arabic: "بدّل {patient} [على {target}]"
 */
export interface PatternTemplate {
  /** Human-readable template string */
  readonly format: string;

  /** Parsed token sequence for matching */
  readonly tokens: PatternToken[];
}

export type PatternToken = LiteralPatternToken | RolePatternToken | GroupPatternToken;

export interface LiteralPatternToken {
  readonly type: 'literal';
  readonly value: string;
  /** Alternative spellings/forms that also match */
  readonly alternatives?: string[];
  /**
   * Parse-only marker: the pattern matcher still consumes this literal, but the
   * renderer skips it. Set when a role declares `renderOverride: { <lang>: '' }`
   * (e.g. fetch's `from`) — the marker is required to PARSE `fetch from /api`
   * yet must be absent from RENDER output, since `fetch from "/api"` is invalid
   * canonical _hyperscript while `fetch "/api"` is valid.
   */
  readonly renderSuppress?: boolean;
}

export interface RolePatternToken {
  readonly type: 'role';
  readonly role: SemanticRole;
  readonly optional?: boolean;
  /** Expected value types (for validation) */
  readonly expectedTypes?: Array<ExpectedType>;
  /**
   * Mirrors the schema's `RoleSpec.valueShape` (see `generators/command-schemas.ts`):
   * the slot's surface form is unambiguous on its own. Carried onto the pattern
   * token because the MATCHER needs it too, not just the confidence model —
   * `'keyword'` slots hold a fixed keyword phrase and are therefore exempt from
   * the trailing-slot verb guard, which exists for marker-less slots that could
   * swallow the next command's verb.
   */
  readonly valueShape?: 'time' | 'reference' | 'keyword' | 'object' | 'clause' | 'list';
}

export interface GroupPatternToken {
  readonly type: 'group';
  readonly tokens: PatternToken[];
  readonly optional?: boolean;
  /**
   * The group is optional for PARSING but belongs in canonical output.
   *
   * Set on the marker-only groups `profile.markersOptional` produces (tr, whose
   * case suffixes may be dropped colloquially, so the parse side must accept
   * both forms). Without it the renderer drops them with every other role-less
   * optional group, and tr rendered `add .selected to #item` as
   * `#item .selected ekle` — no case markers at all, where the corpus has
   * `#item e .selected i ekle`.
   */
  readonly renderRequired?: boolean;
}

/**
 * Rules for extracting semantic values from matched tokens.
 */
export interface ExtractionRules {
  readonly [role: string]: ExtractionRule;
}

export interface ExtractionRule {
  /** Position-based extraction (0-indexed from pattern start) */
  readonly position?: number;
  /** Marker-based extraction (find value after this marker) */
  readonly marker?: string;
  /** Alternative markers that also work */
  readonly markerAlternatives?: string[];
  /** Transform the extracted value */
  readonly transform?: (raw: string) => SemanticValue;
  /** Default value if not found (for optional roles) */
  readonly default?: SemanticValue;
  /** Static value extraction (for event handler wrapped commands) */
  readonly value?: string;
  /**
   * The static `value` is an expression, as the English parse types the word
   * (`go back`'s destination), not a literal.
   */
  readonly valueIsExpression?: true;
  /** Extract value from a pattern role by name */
  readonly fromRole?: string;
}

/**
 * Additional constraints on pattern applicability.
 */
export interface PatternConstraints {
  /** Required roles that must be present */
  readonly requiredRoles?: SemanticRole[];
  /** Roles that must NOT be present */
  readonly forbiddenRoles?: SemanticRole[];
  /** Valid selector types for the patient role */
  readonly validPatientTypes?: Array<SelectorValue['selectorKind']>;
  /** Pattern IDs this conflicts with */
  readonly conflictsWith?: string[];
}

// =============================================================================
// Token Stream (for pattern matching)
// =============================================================================

/**
 * A token from language-specific tokenization.
 */
export interface LanguageToken {
  readonly value: string;
  readonly kind: TokenKind;
  readonly position: SourcePosition;
  /** Normalized form from explicit keyword map (e.g., 切り替え → toggle) */
  readonly normalized?: string;
  /** Morphologically normalized stem (e.g., 切り替えた → 切り替え) */
  readonly stem?: string;
  /** Confidence in the morphological stem (0.0-1.0) */
  readonly stemConfidence?: number;
  /** Additional metadata for specific token types (e.g., event modifier data) */
  readonly metadata?: Record<string, unknown>;
}

export type TokenKind =
  | 'keyword' // Command or modifier keyword
  | 'selector' // CSS selector (#id, .class, [attr])
  | 'literal' // String or number literal
  | 'particle' // Grammatical particle (を, に, من)
  | 'conjunction' // Grammatical conjunction (Arabic و/ف proclitics)
  | 'event-modifier' // Event modifier (.once, .debounce(300), .throttle(100))
  | 'identifier' // Variable or property name
  | 'operator' // Comparison or logical operator
  | 'punctuation' // Brackets, quotes, etc.
  | 'url'; // URL/path (/path, https://...)

/**
 * A stream of tokens with navigation capabilities.
 */
export interface TokenStream {
  readonly tokens: readonly LanguageToken[];
  readonly language: string;

  /** Look at token at current position + offset without consuming */
  peek(offset?: number): LanguageToken | null;

  /** Consume and return current token, advance position */
  advance(): LanguageToken;

  /** Check if we've consumed all tokens */
  isAtEnd(): boolean;

  /** Save current position for backtracking */
  mark(): StreamMark;

  /** Restore to a saved position */
  reset(mark: StreamMark): void;

  /** Get current position */
  position(): number;
}

export interface StreamMark {
  readonly position: number;
}

// =============================================================================
// Pattern Matching Results
// =============================================================================

/**
 * Result of successfully matching a pattern.
 */
export interface PatternMatchResult {
  readonly pattern: LanguagePattern;
  readonly captured: ReadonlyMap<SemanticRole, SemanticValue>;
  readonly consumedTokens: number;
  readonly confidence: number; // 0-1, how well the pattern matched
  /**
   * Stream index at which each captured role's value STARTED, for callers that
   * need to re-read a slot's own span rather than the tail after it. Populated
   * on the same success path as `captured`, so it has exactly the same fidelity
   * (a role left behind by a rolled-back group survives in both maps or in
   * neither). Consumed by the fused-handler body rewind in `semantic-parser`.
   */
  readonly roleStarts?: ReadonlyMap<SemanticRole, number>;
  /**
   * A source phrase the matcher found in the event HEAD (`from #btn` right
   * after the event, `#btn から` before the next head literal) rather than in a
   * pattern slot. Not in `captured`, so a fused pattern's own `[de {source}]`
   * group (the body command's from-phrase) can neither overwrite it nor be
   * overwritten. Whose it is — the handler's `from`, or in a postpositional
   * language the fused body command's own source — is the parser's call.
   */
  readonly eventSource?: SemanticValue;
}

/**
 * Error when pattern matching fails.
 */
export interface PatternMatchError {
  readonly message: string;
  readonly position: SourcePosition;
  readonly expectedPatterns?: string[];
  readonly partialMatch?: Partial<PatternMatchResult>;
}

// =============================================================================
// Tokenizer Interface
// =============================================================================

/**
 * Language-specific tokenizer interface.
 * Each language implements its own tokenizer to handle:
 * - Word boundaries (spaces for English, particles for Japanese)
 * - Character sets (ASCII, CJK, Arabic, etc.)
 * - Special markers (particles, prefixes, suffixes)
 */
export interface LanguageTokenizer {
  readonly language: string;
  readonly direction: 'ltr' | 'rtl';

  /** Convert input string to token stream */
  tokenize(input: string): TokenStream;

  /** Classify a single token */
  classifyToken(token: string): TokenKind;
}

// =============================================================================
// Semantic Parser Interface
// =============================================================================

/**
 * Main parser interface - converts natural language to semantic nodes.
 */
export interface SemanticParser {
  /** Parse input in specified language to semantic node */
  parse(input: string, language: string): SemanticNode;

  /** Check if input can be parsed in the specified language */
  canParse(input: string, language: string): boolean;

  /** Get all supported languages */
  supportedLanguages(): string[];
}

// =============================================================================
// Semantic Renderer Interface
// =============================================================================

/**
 * Renderer interface - converts semantic nodes to natural language.
 */
export interface SemanticRenderer {
  /** Render semantic node in specified language */
  render(node: SemanticNode, language: string): string;

  /** Render semantic node in explicit mode */
  renderExplicit(node: SemanticNode): string;

  /** Get all supported languages */
  supportedLanguages(): string[];
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create a selector semantic value from a CSS selector string.
 */
export function createSelector(value: string): SelectorValue {
  let selectorKind: SelectorValue['selectorKind'] = 'complex';

  if (value.startsWith('#') && !value.includes(' ')) {
    selectorKind = 'id';
  } else if (value.startsWith('.') && !value.includes(' ')) {
    selectorKind = 'class';
  } else if (value.startsWith('[') && value.endsWith(']')) {
    selectorKind = 'attribute';
  } else if (/^[a-z][a-z0-9]*$/i.test(value)) {
    selectorKind = 'element';
  }

  return { type: 'selector', value, selectorKind };
}

/**
 * Create a literal semantic value.
 */
export function createLiteral(
  value: string | number | boolean,
  dataType?: LiteralValue['dataType']
): LiteralValue {
  const result: LiteralValue = { type: 'literal', value };
  if (dataType !== undefined) {
    return { type: 'literal', value, dataType };
  }
  return result;
}

/**
 * The value a constant keyword stands for, by its normalized form: `true` and
 * `false` are boolean literals, and `null` is the expression the AST builder
 * reads as a real null (a literal cannot hold one). Undefined for any other
 * word.
 *
 * The keyword used to become an untyped STRING literal, so a translated
 * `set #b.disabled to false` stored the truthy string "false" on the direct
 * path and the button stayed disabled.
 */
export function createConstant(normalized: string): LiteralValue | ExpressionValue | undefined {
  if (normalized === 'true') return createLiteral(true, 'boolean');
  if (normalized === 'false') return createLiteral(false, 'boolean');
  if (normalized === 'null') return { type: 'expression', raw: 'null' };
  return undefined;
}

/**
 * Check if a string is a valid reference keyword.
 */
import { isValidReference as isValidReferenceBase } from '@lokascript/framework';

/**
 * Check if a value is a valid hyperscript reference.
 * Delegates to @lokascript/framework/ir with the default reference set.
 */
export function isValidReference(value: string): value is ReferenceValue['value'] {
  return isValidReferenceBase(value);
}

/**
 * Create a reference semantic value.
 */
export function createReference(value: ReferenceValue['value']): ReferenceValue {
  return { type: 'reference', value };
}

/**
 * Stamp a captured value with the span of the token run it came from.
 *
 * A no-op when the caller has no span, so an absent
 * {@link SourceSpanned.position} keeps meaning "not recorded" rather than
 * "offset zero". Called from `PatternMatcher.stampCaptureSpan`, which is the
 * single place a role acquires one.
 */
export function withPosition<V extends SemanticValue>(
  value: V,
  position: SourcePosition | undefined
): V {
  return position ? ({ ...value, position } as V) : value;
}

/**
 * Create a property path semantic value.
 *
 * `access` records which surface was matched (`#input.value` vs `#input's
 * value`) so the renderer can reproduce it instead of guessing; see
 * `PropertyPathValue.access`. Omit it when the caller genuinely does not know —
 * the renderer then falls back to the possessive construction it has always
 * used.
 */
export function createPropertyPath(
  object: SemanticValue,
  property: string,
  access?: PropertyPathValue['access']
): PropertyPathValue {
  return access
    ? { type: 'property-path', object, property, access }
    : { type: 'property-path', object, property };
}

/**
 * Create a semantic node with the given action and roles.
 */
export function createCommandNode(
  action: ActionType,
  roles: Record<string, SemanticValue>,
  metadata?: SemanticMetadata
): CommandSemanticNode {
  const node: CommandSemanticNode = {
    kind: 'command',
    action,
    roles: new Map(Object.entries(roles) as [SemanticRole, SemanticValue][]),
  };
  if (metadata !== undefined) {
    return { ...node, metadata };
  }
  return node;
}

/**
 * Create an event handler semantic node.
 */
export function createEventHandler(
  event: SemanticValue,
  body: SemanticNode[],
  modifiers?: EventModifiers,
  metadata?: SemanticMetadata,
  parameterNames?: string[],
  additionalEvents?: SemanticValue[]
): EventHandlerSemanticNode {
  const roles = new Map<SemanticRole, SemanticValue>();
  roles.set('event', event);

  const node: EventHandlerSemanticNode = {
    kind: 'event-handler',
    action: 'on',
    roles,
    body,
  };

  if (modifiers !== undefined) {
    (node as { eventModifiers?: EventModifiers }).eventModifiers = modifiers;
  }
  if (metadata !== undefined) {
    (node as { metadata?: SemanticMetadata }).metadata = metadata;
  }
  if (parameterNames !== undefined && parameterNames.length > 0) {
    (node as { parameterNames?: readonly string[] }).parameterNames = parameterNames;
  }
  if (additionalEvents !== undefined && additionalEvents.length > 0) {
    (node as { additionalEvents?: readonly SemanticValue[] }).additionalEvents = additionalEvents;
  }

  return node;
}

/**
 * Create a behavior semantic node (a `behavior Name(params) … end` block).
 */
export function createBehaviorNode(
  name: string,
  parameters: string[],
  eventHandlers: EventHandlerSemanticNode[],
  initBlock?: SemanticNode[],
  metadata?: SemanticMetadata
): BehaviorSemanticNode {
  const node: BehaviorSemanticNode = {
    kind: 'behavior',
    action: 'behavior',
    roles: new Map(),
    name,
    parameters,
    eventHandlers,
  };
  if (initBlock !== undefined && initBlock.length > 0) {
    (node as { initBlock?: SemanticNode[] }).initBlock = initBlock;
  }
  if (metadata !== undefined) {
    (node as { metadata?: SemanticMetadata }).metadata = metadata;
  }
  return node;
}

/**
 * Create a function-definition semantic node (a `def name(params) … end` block).
 */
export function createDefNode(
  name: string,
  parameters: string[],
  body: SemanticNode[],
  metadata?: SemanticMetadata
): DefSemanticNode {
  const node: DefSemanticNode = {
    kind: 'def',
    action: 'def' as ActionType,
    roles: new Map(),
    name,
    parameters,
    body,
  };
  if (metadata !== undefined) {
    (node as { metadata?: SemanticMetadata }).metadata = metadata;
  }
  return node;
}

/**
 * Create a feature semantic node (a `live`/`eventsource`/`socket`/`worker`/
 * `intercept` block). `name` is omitted for the unnamed features (`live`,
 * `intercept`); `body` is empty for `intercept`, whose body is opaque.
 */
export function createFeatureNode(
  action: FeatureAction,
  body: SemanticNode[],
  name?: string,
  metadata?: SemanticMetadata,
  /** Head roles — only the reactive `when` carries one (`condition`). */
  roles?: ReadonlyMap<SemanticRole, SemanticValue>
): FeatureSemanticNode {
  const node: FeatureSemanticNode = {
    kind: 'feature',
    action,
    roles: roles ?? new Map(),
    body,
  };
  if (name !== undefined) {
    (node as { name?: string }).name = name;
  }
  if (metadata !== undefined) {
    (node as { metadata?: SemanticMetadata }).metadata = metadata;
  }
  return node;
}

/**
 * Create a compound semantic node (for chained statements).
 */
export function createCompoundNode(
  statements: SemanticNode[],
  chainType: 'then' | 'and' | 'async' = 'then',
  metadata?: SemanticMetadata
): CompoundSemanticNode {
  const node: CompoundSemanticNode = {
    kind: 'compound',
    action: 'compound' as ActionType, // Compound doesn't have a specific action
    roles: new Map(),
    statements,
    chainType,
  };
  if (metadata !== undefined) {
    (node as { metadata?: SemanticMetadata }).metadata = metadata;
  }
  return node;
}

/**
 * Create a conditional semantic node (if/else).
 */
export function createConditionalNode(
  condition: SemanticValue,
  thenBranch: SemanticNode[],
  elseBranch?: SemanticNode[],
  metadata?: SemanticMetadata
): ConditionalSemanticNode {
  const roles = new Map<SemanticRole, SemanticValue>();
  roles.set('condition' as SemanticRole, condition);

  const node: ConditionalSemanticNode = {
    kind: 'conditional',
    action: 'if',
    roles,
    thenBranch,
  };
  if (elseBranch !== undefined) {
    (node as { elseBranch?: SemanticNode[] }).elseBranch = elseBranch;
  }
  if (metadata !== undefined) {
    (node as { metadata?: SemanticMetadata }).metadata = metadata;
  }
  return node;
}

/**
 * Create a loop semantic node.
 */
export function createLoopNode(
  action: 'repeat' | 'for' | 'while',
  loopVariant: LoopVariant,
  roles: Record<string, SemanticValue>,
  body: SemanticNode[],
  options?: {
    loopVariable?: string;
    indexVariable?: string;
    metadata?: SemanticMetadata;
    bottomTested?: boolean;
  }
): LoopSemanticNode {
  const node: LoopSemanticNode = {
    kind: 'loop',
    action,
    loopVariant,
    roles: new Map(Object.entries(roles) as [SemanticRole, SemanticValue][]),
    body,
  };

  if (options?.loopVariable) {
    (node as { loopVariable?: string }).loopVariable = options.loopVariable;
  }
  if (options?.indexVariable) {
    (node as { indexVariable?: string }).indexVariable = options.indexVariable;
  }
  if (options?.metadata) {
    (node as { metadata?: SemanticMetadata }).metadata = options.metadata;
  }
  if (options?.bottomTested) {
    (node as { bottomTested?: boolean }).bottomTested = true;
  }

  return node;
}

// =============================================================================
// Semantic Parse Result (for validation)
// =============================================================================

/**
 * Argument with semantic role attached.
 */
export type SemanticArgument = SemanticValue & {
  role?: SemanticRole;
};

/**
 * Result of semantic parsing (used by command validator).
 */
export interface SemanticParseResult {
  /** The action/command type */
  readonly action: ActionType;
  /** Confidence score (0-1) */
  readonly confidence: number;
  /** Source language code */
  readonly language: string;
  /** Parsed arguments with roles */
  readonly arguments: SemanticArgument[];
}
